import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Smartphone,
  Download,
  Users,
  Settings,
  QrCode,
  Copy,
  Check,
  ExternalLink,
  Shield,
  Bell,
  MapPin,
  Camera,
  Wifi,
  User,
  Plus,
  Trash2,
  Search,
  Loader2,
  CheckCircle2,
  XCircle,
  Link2,
  Share2,
  MessageCircle,
  Package,
  Navigation
} from "lucide-react";

export default function AppMovilConfigPage() {
  const [users, setUsers] = useState([]);
  const [mensajeros, setMensajeros] = useState([]);
  const [authorizedUsers, setAuthorizedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [copied, setCopied] = useState(false);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [saving, setSaving] = useState(false);
  
  // App settings
  const [settings, setSettings] = useState({
    gps_enabled: true,
    camera_enabled: true,
    notifications_enabled: true,
    signature_required: true,
    photo_required: false,
    auto_assign_deliveries: false
  });

  const { getAuthHeaders } = useAuth();

  // Get the app URL - unified PWA with integrated chat
  const appUrl = `${window.location.origin}/app/entregas`;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, mensajerosRes, settingsRes] = await Promise.all([
        axios.get(`${API}/users`, getAuthHeaders()),
        axios.get(`${API}/mensajeros`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/app/settings`, getAuthHeaders()).catch(() => ({ data: null }))
      ]);
      
      setUsers(usersRes.data || []);
      setMensajeros(mensajerosRes.data || []);
      
      // Get users with mensajero or recepcion role (those who can use the app)
      const appUsers = usersRes.data.filter(u => 
        u.role?.toLowerCase().includes('mensajero') || 
        u.role?.toLowerCase().includes('recepcion') ||
        u.role?.toLowerCase() === 'admin' ||
        u.role?.toLowerCase() === 'administrador'
      );
      setAuthorizedUsers(appUsers);
      
      if (settingsRes.data) {
        setSettings(settingsRes.data);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Enlace copiado al portapapeles");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Error al copiar");
    }
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'QAsesores - App de Mensajería',
          text: 'Descarga la aplicación de mensajería de QAsesores',
          url: appUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error("Error al compartir");
        }
      }
    } else {
      copyToClipboard(appUrl);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/app/settings`, settings, getAuthHeaders());
      toast.success("Configuración guardada");
    } catch (err) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const search = searchTerm.toLowerCase();
    return (
      u.name?.toLowerCase().includes(search) ||
      u.email?.toLowerCase().includes(search) ||
      u.role?.toLowerCase().includes(search)
    );
  });

  const canUseApp = (user) => {
    const role = user.role?.toLowerCase() || '';
    return role.includes('mensajero') || 
           role.includes('recepcion') || 
           role === 'admin' || 
           role === 'administrador';
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="app-movil-config-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope'] flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0C314C] to-[#2a4d7a] flex items-center justify-center">
                <Smartphone className="w-6 h-6 text-[#45E69A]" />
              </div>
              Aplicación Móvil
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Configura y gestiona el acceso a la aplicación móvil para mensajeros
            </p>
          </div>
        </div>

        <Tabs defaultValue="instalacion" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 max-w-lg">
            <TabsTrigger value="instalacion">
              <Download className="w-4 h-4 mr-2" />
              Instalación
            </TabsTrigger>
            <TabsTrigger value="acceso">
              <Users className="w-4 h-4 mr-2" />
              Acceso
            </TabsTrigger>
            <TabsTrigger value="configuracion">
              <Settings className="w-4 h-4 mr-2" />
              Configuración
            </TabsTrigger>
          </TabsList>

          {/* Installation Tab */}
          <TabsContent value="instalacion" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Install Card */}
              <Card className="border-slate-200 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Download className="w-5 h-5 text-[#0C314C]" />
                    Instalar Aplicación
                  </CardTitle>
                  <CardDescription>
                    La aplicación se puede instalar directamente desde el navegador del celular
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4 space-y-3">
                    <h4 className="font-medium text-sm text-slate-700 dark:text-slate-300">
                      Pasos para instalar:
                    </h4>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-slate-600 dark:text-slate-400">
                      <li>Abre el enlace en el navegador del celular</li>
                      <li>
                        <span className="font-medium">En Chrome:</span> Menú (⋮) → "Añadir a pantalla de inicio"
                      </li>
                      <li>
                        <span className="font-medium">En Safari:</span> Compartir (⬆️) → "Añadir a pantalla de inicio"
                      </li>
                      <li>La app se instalará como aplicación nativa</li>
                    </ol>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Enlace de la Aplicación</Label>
                    <div className="flex gap-2">
                      <Input 
                        value={appUrl} 
                        readOnly 
                        className="bg-white dark:bg-slate-800 font-mono text-sm"
                      />
                      <Button
                        variant="outline"
                        onClick={() => copyToClipboard(appUrl)}
                        className="shrink-0"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      onClick={shareLink}
                      className="flex-1 bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Compartir Enlace
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => window.open(appUrl, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Abrir
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Features Card */}
              <Card className="border-slate-200 dark:border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-[#0C314C]" />
                    Funcionalidades
                  </CardTitle>
                  <CardDescription>
                    Características disponibles en la aplicación móvil
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
                        <Package className="w-5 h-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-slate-900 dark:text-white">Gestión de Entregas</p>
                        <p className="text-xs text-slate-500">Tomar y confirmar</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                      <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center">
                        <Navigation className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-slate-900 dark:text-white">Navegación GPS</p>
                        <p className="text-xs text-slate-500">Google Maps</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                      <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-800 flex items-center justify-center">
                        <MessageCircle className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-slate-900 dark:text-white">Chat en Tiempo Real</p>
                        <p className="text-xs text-slate-500">Comunicación</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                      <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-800 flex items-center justify-center">
                        <Camera className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <p className="font-medium text-sm text-slate-900 dark:text-white">Firma y Foto</p>
                        <p className="text-xs text-slate-500">Confirmación</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-slate-100 dark:bg-slate-800 rounded-lg">
                    <div className="flex items-center gap-2 text-sm">
                      <Wifi className="w-4 h-4 text-green-500" />
                      <span className="text-slate-700 dark:text-slate-300">
                        Comunicación en tiempo real vía WebSocket
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* QR Code Card */}
              <Card className="border-slate-200 dark:border-slate-700 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <QrCode className="w-5 h-5 text-[#0C314C]" />
                    Enlaces Rápidos
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-slate-900 dark:text-white">App de Entregas</span>
                        <Badge variant="outline" className="bg-green-100 text-green-700">Principal</Badge>
                      </div>
                      <code className="text-xs text-slate-500 break-all">{appUrl}</code>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" onClick={() => copyToClipboard(appUrl)}>
                          <Copy className="w-3 h-3 mr-1" />
                          Copiar
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => window.open(appUrl, '_blank')}>
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Abrir
                        </Button>
                      </div>
                    </div>
                    
                    <div className="p-4 border border-slate-200 dark:border-slate-700 rounded-lg bg-blue-50/50 dark:bg-blue-900/20">
                      <div className="flex items-center gap-2 mb-2">
                        <MessageCircle className="w-4 h-4 text-blue-600" />
                        <span className="font-medium text-slate-900 dark:text-white">Chat Integrado</span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        El chat está integrado directamente en la aplicación de entregas. 
                        Los mensajeros pueden acceder al chat desde la barra de navegación inferior.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Access Tab */}
          <TabsContent value="acceso" className="space-y-6">
            <Card className="border-slate-200 dark:border-slate-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-[#0C314C]" />
                      Usuarios con Acceso
                    </CardTitle>
                    <CardDescription>
                      Los usuarios con rol "Mensajero", "Recepción" o "Administrador" tienen acceso a la aplicación
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Buscar usuario..."
                      className="pl-10"
                    />
                  </div>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredUsers.map((user) => (
                      <div 
                        key={user.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          canUseApp(user) 
                            ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
                            : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            canUseApp(user) ? 'bg-green-100 dark:bg-green-800' : 'bg-slate-200 dark:bg-slate-700'
                          }`}>
                            <User className={`w-5 h-5 ${
                              canUseApp(user) ? 'text-green-600 dark:text-green-400' : 'text-slate-500'
                            }`} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">{user.name}</p>
                            <p className="text-xs text-slate-500">{user.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge 
                            variant="outline"
                            className={
                              canUseApp(user)
                                ? 'bg-green-100 text-green-700 border-green-300'
                                : 'bg-slate-100 text-slate-600'
                            }
                          >
                            {user.role || "Sin rol"}
                          </Badge>
                          {canUseApp(user) ? (
                            <CheckCircle2 className="w-5 h-5 text-green-500" />
                          ) : (
                            <XCircle className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    <strong>Nota:</strong> Para dar acceso a un usuario a la aplicación móvil, 
                    asígnele uno de los siguientes roles desde la sección de Usuarios:
                  </p>
                  <ul className="mt-2 text-sm text-blue-600 dark:text-blue-400 list-disc list-inside">
                    <li><strong>Mensajero</strong> - Acceso completo a entregas</li>
                    <li><strong>Recepción</strong> - Gestión de correspondencia</li>
                    <li><strong>Administrador</strong> - Acceso total</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Mensajeros Card */}
            <Card className="border-slate-200 dark:border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#0C314C]" />
                  Mensajeros Registrados
                </CardTitle>
                <CardDescription>
                  Lista de mensajeros que pueden recibir y entregar correspondencia
                </CardDescription>
              </CardHeader>
              <CardContent>
                {mensajeros.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No hay mensajeros registrados</p>
                    <p className="text-xs mt-1">Registre mensajeros en Mensajería → Mensajeros</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {mensajeros.map((m) => (
                      <div 
                        key={m.id}
                        className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg"
                      >
                        <div className="w-10 h-10 rounded-full bg-[#0C314C] flex items-center justify-center">
                          <User className="w-5 h-5 text-[#45E69A]" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-white">{m.name}</p>
                          <p className="text-xs text-slate-500">{m.telefono || "Sin teléfono"}</p>
                        </div>
                        <Badge 
                          variant="outline" 
                          className={m.activo ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}
                        >
                          {m.activo ? "Activo" : "Inactivo"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="configuracion" className="space-y-6">
            <Card className="border-slate-200 dark:border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5 text-[#0C314C]" />
                  Configuración de la Aplicación
                </CardTitle>
                <CardDescription>
                  Personaliza el comportamiento de la aplicación móvil
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6">
                  {/* GPS Setting */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center">
                        <MapPin className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">Navegación GPS</p>
                        <p className="text-sm text-slate-500">Permitir abrir direcciones en Google Maps</p>
                      </div>
                    </div>
                    <Switch
                      checked={settings.gps_enabled}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, gps_enabled: checked }))}
                    />
                  </div>

                  {/* Camera Setting */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-800 flex items-center justify-center">
                        <Camera className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">Cámara</p>
                        <p className="text-sm text-slate-500">Permitir tomar fotos de confirmación</p>
                      </div>
                    </div>
                    <Switch
                      checked={settings.camera_enabled}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, camera_enabled: checked }))}
                    />
                  </div>

                  {/* Notifications Setting */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-800 flex items-center justify-center">
                        <Bell className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">Notificaciones</p>
                        <p className="text-sm text-slate-500">Enviar alertas de nuevas entregas</p>
                      </div>
                    </div>
                    <Switch
                      checked={settings.notifications_enabled}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, notifications_enabled: checked }))}
                    />
                  </div>

                  {/* Signature Required */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
                        <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">Firma Obligatoria</p>
                        <p className="text-sm text-slate-500">Requerir firma digital para confirmar entrega</p>
                      </div>
                    </div>
                    <Switch
                      checked={settings.signature_required}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, signature_required: checked }))}
                    />
                  </div>

                  {/* Photo Required */}
                  <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-800 flex items-center justify-center">
                        <Camera className="w-5 h-5 text-red-600 dark:text-red-400" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">Foto Obligatoria</p>
                        <p className="text-sm text-slate-500">Requerir foto para confirmar entrega</p>
                      </div>
                    </div>
                    <Switch
                      checked={settings.photo_required}
                      onCheckedChange={(checked) => setSettings(prev => ({ ...prev, photo_required: checked }))}
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-slate-200 dark:border-slate-700">
                  <Button 
                    onClick={saveSettings}
                    disabled={saving}
                    className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Guardar Configuración
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
